import { createSelector } from 'reselect';
import { get, sortBy, reverse, reduce } from 'lodash';
import parseDate from 'date-fns/parse';

import getPageType from './getPageType';
import { TrackingClient } from '@sparefoot/tracking-client';

export const UnitReservationFormSelector = createSelector(
	(state) => state.app.recaptchaSiteKey,
	(state) => state.bookings.trackedBooking,
	(_, { facility }) =>
		get(facility, 'closures', []).map((closure) => parseDate(closure)),
	(recaptchaSiteKey, trackedBooking, closures) => ({
		recaptchaSiteKey,
		trackedBooking,
		closures,
	})
);

export const getConfirmationCodeFromRoute = (state, props) =>
	get(props, 'match.params.confirmationCode');

export const getBookingFromRoute = createSelector(
	getConfirmationCodeFromRoute,
	(state) => state.bookings.byId,
	(confirmationCode, bookings) => bookings[confirmationCode]
);

export const getRecentBookings = createSelector(
	(state) => state.bookings.recentBookings,
	(state) => state.bookings.byId,
	(recentBookings, bookings) =>
		reverse(
			sortBy(
				recentBookings.map((bookingId) => bookings[bookingId]),
				'timestamp'
			)
		)
);

export function getRecentBookingsFacilities(recentBookings, facilities) {
	return reduce(
		recentBookings,
		(acc, booking) => ({
			...acc,
			[booking.bookedFacility.facilityId]:
				facilities[booking.bookedFacility.facilityId],
		}),
		{}
	);
}

export function getRecentBookingsUnits(recentBookings, units) {
	return reduce(
		recentBookings,
		(acc, booking) => ({
			...acc,
			[booking.bookedUnit.id]: units[booking.bookedUnit.id],
		}),
		{}
	);
}

export function getBookingAuthentication(bookingResponse, confirmationCode) {
	const booking = bookingResponse.payload.entities.bookings[confirmationCode];
	return {
		isAuthorized: booking.bookingAuth.isAuthBookingAuth,
		newToken: booking.bookingAuth?.bookingToken,
		hasError: !booking.bookingAuth.isAuthBookingAuth,
		bookingEmail:
			bookingResponse.payload.entities.bookings[confirmationCode].customer
				.email,
	};
}

export const getBookingConfirmation = createSelector(
	getBookingFromRoute,
	getPageType,
	(state) => state.facilities.byId,
	(state) => state.units.byId,
	getRecentBookings,
	(state) => state.pageMeta.phone,
	(state) => state.app.baseUrl,
	(state) => state.app.omiAppUrl,
	(state) => state.app.omiAccessKey,
	(
		booking,
		pageType,
		facilities,
		units,
		recentBookings,
		phone,
		baseUrl,
		omiAppUrl,
		omiAccessKey
	) => ({
		booking,
		pageType,
		facility: !!booking && facilities[booking.bookedFacility.facilityId],
		unit: !!booking && units[booking.bookedUnit.id],
		recentBookingsFacilities: getRecentBookingsFacilities(
			recentBookings,
			facilities
		),
		recentBookingsUnits: getRecentBookingsUnits(recentBookings, units),
		recentBookings,
		phone,
		baseUrl,
		omiAppUrl,
		omiAccessKey,
		isAuthorized: !!booking && booking.isAuthorized,
	})
);

export const getPhoneAndBookedFacility = createSelector(
	getBookingFromRoute,
	getPageType,
	(state) => state.facilities.byId,
	(state) => state.pageMeta.phone,
	(booking, pageType, facilities, phone) => ({
		phone,
		pageType,
		facility: !!booking && facilities[booking.bookedFacility.facilityId],
	})
);

export const getBookingOffer = createSelector(
	getPageType,
	getBookingFromRoute,
	(state) => state.bookings.offer,
	(pageType, booking, offer, documentKeys) => ({
		pageType,
		booking,
		offer,
		documentKeys,
	})
);

// Selectors For Move In Page

export const getOMIStartedTrackingProps = createSelector(
	getBookingFromRoute,
	(booking) => ({
		omiTrackingProps: {
			reservation_confirmation: booking.confirmationCode,
			unit: {
				currency: 'usd',
				id: booking.bookedUnit.id,
				variant_size: `${booking.bookedUnit.description.physical.length} x ${booking.bookedUnit.description.physical.width}`,
				price: booking.bookedUnit.price.meta,
				quantity: 1,
			},
		},
	})
);

export const getOMIEmbedProps = createSelector(
	getBookingFromRoute,
	getOMIStartedTrackingProps,
	(state) => state.app.omiAppUrl,
	(state) => state.app.omiAccessKey,
	(booking, trackingProps, omiAppUrl, omiAccessKey) => ({
		omiAppUrl,
		omiAttributes: booking.omiAttributes,
		omiUnitId: booking.omiUnitId,
		omiTrackingProps: trackingProps,
		omiAccessKey,
	})
);

export const getBookedFacility = createSelector(
	getBookingFromRoute,
	(state) => state.facilities.byId,
	(booking, facilities) => facilities[booking.bookedFacility.facilityId]
);

export const getMoveInHeroProps = createSelector(
	getBookedFacility,
	(facility) => ({
		facilityImage: facility.images[0],
		facilityName: facility.name,
	})
);

export const getMoveInPageProps = createSelector(
	getOMIEmbedProps,
	getMoveInHeroProps,
	(omiEmbedProps, moveInPageHeroProps) => ({
		getOMIStartedTrackingProps,
		omiEmbedProps,
		moveInPageHeroProps,
	})
);
